import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ChevronDown,
  ChevronRight,
  CircleSlash,
  Clock,
  ExternalLink,
  Loader2,
  Pause,
} from "lucide-react";
import { api, type AutomationUnit } from "@/lib/api";
import { formatItalianDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const HEALTH: Record<string, { dot: string; label: string }> = {
  ok: { dot: "bg-emerald-500", label: "attivo" },
  error: { dot: "bg-red-500", label: "errore" },
  off: { dot: "bg-slate-300", label: "spento" },
  idle: { dot: "bg-amber-400", label: "a riposo" },
  unknown: { dot: "bg-slate-300", label: "?" },
};

function UnitRow({ u }: { u: AutomationUnit }) {
  const [open, setOpen] = useState(false);
  const logs = useQuery({
    queryKey: ["automation-logs", u.unit],
    queryFn: () => api.automationLogs(u.unit, 100),
    enabled: open,
  });
  const h = HEALTH[u.health] ?? HEALTH.unknown;
  return (
    <div className="border-t border-slate-100 first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-1 py-2 text-left hover:bg-slate-50"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        )}
        <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", h.dot)} title={h.label} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-slate-800">{u.label}</span>
            <span className="hidden text-[11px] text-slate-400 sm:inline">{u.desc}</span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
            <span className="font-mono">{u.unit}</span>
            {u.last_run ? (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> ultimo {formatItalianDateTime(u.last_run)}
              </span>
            ) : null}
            {u.next_run ? (
              <span className="inline-flex items-center gap-1 text-court-600">
                → prossimo {formatItalianDateTime(u.next_run)}
              </span>
            ) : null}
            {u.enabled === "disabled" ? (
              <span className="inline-flex items-center gap-1 text-amber-600">
                <Pause className="h-3 w-3" /> disabilitato
              </span>
            ) : null}
          </div>
        </div>
        <span className="shrink-0 text-[10px] uppercase text-slate-400">{u.active_state}</span>
      </button>
      {open ? (
        <div className="px-2 pb-2">
          {logs.isLoading ? (
            <div className="flex items-center gap-2 p-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carico log…
            </div>
          ) : logs.isError ? (
            <div className="p-2 text-xs text-red-600">Log non disponibili</div>
          ) : (
            <pre className="max-h-64 overflow-auto rounded-md bg-slate-900 p-2.5 font-mono text-[10px] leading-relaxed text-slate-300">
              {(logs.data?.lines ?? []).join("\n") || "(nessun log)"}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function Automations() {
  const q = useQuery({
    queryKey: ["automations"],
    queryFn: () => api.listAutomations(),
    refetchInterval: 15000,
  });
  const pickle = useQuery({
    queryKey: ["pickleball-overview"],
    queryFn: () => api.pickleballOverview(),
    refetchInterval: 60000,
    retry: false,
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Activity className="h-6 w-6 text-moving-600" /> Automazioni & Sistemi
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Stato di tutti i timer e servizi automatici Moving Padel. Sola lettura
            (monitoraggio + log) — nessun avvio/arresto da qui.
          </p>
        </div>
        {q.data ? (
          <div className="flex shrink-0 gap-2">
            <Badge n={q.data.summary.ok} label="attivi" cls="bg-emerald-100 text-emerald-700" />
            {q.data.summary.error > 0 ? (
              <Badge n={q.data.summary.error} label="errore" cls="bg-red-100 text-red-700" />
            ) : null}
            <Badge n={q.data.summary.off} label="spenti" cls="bg-slate-100 text-slate-600" />
          </div>
        ) : null}
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Carico stato sistemi…
        </div>
      ) : q.isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Impossibile leggere lo stato dei sistemi.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {q.data?.systems.map((s) => (
            <section key={s.key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-lg">{s.icon}</span>
                <h2 className="font-semibold text-slate-900">{s.label}</h2>
                {s.external_url ? (
                  <a
                    href={s.external_url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-xs text-court-600 hover:underline"
                  >
                    apri <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </div>
              {s.external_url && pickle.data ? (
                <div className="mb-2 grid grid-cols-4 gap-2 rounded-lg bg-slate-50 p-2 text-center">
                  <KpiMini label="utenti" value={pickle.data.users_total} />
                  <KpiMini label="oggi" value={pickle.data.today_bookings} />
                  <KpiMini label="prossime" value={pickle.data.upcoming_bookings} />
                  <KpiMini label="incassi" value={`€${pickle.data.revenue_confirmed_eur}`} />
                </div>
              ) : null}
              <div>
                {s.units.map((u) => (
                  <UnitRow key={u.unit} u={u} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function KpiMini({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-sm font-bold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase text-slate-400">{label}</div>
    </div>
  );
}

function Badge({ n, label, cls }: { n: number; label: string; cls: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold", cls)}>
      {n === 0 ? <CircleSlash className="h-3 w-3" /> : null}
      {n} {label}
    </span>
  );
}
